import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { UpdateStatusDTO } from '../../global';
import { useLanguage } from '../../lib/i18n';
import { IS_NATIVE, IS_WEB } from '../../lib/platform';

/**
 * Sunucu bu sürümü artık kabul etmiyor (426 `upgrade_required`, yönetim konsolu >
 * Sistem > En düşük uygulama sürümü). Veri biçimi değişen geçişlerde (ör. içerik
 * şifrelemesi) eski sürüm yeni veriyi okuyamaz ya da düz metin yazar; bu yüzden
 * güncellenene kadar uygulama kapalı. Kayıt sürerken açılmaz (App.tsx).
 *
 * Masaüstü: güncelleme denetlenir, arka planda iner, "Yeniden başlat" kurar.
 * Web: sayfayı yenilemek yeni sürümü getirir.
 */
export const UpdateRequiredView: React.FC<{ minVersion: string | null; onSignOut: () => void }> = ({ minVersion, onSignOut }) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState<UpdateStatusDTO | null>(null);

  useEffect(() => {
    if (IS_WEB) return;
    const unsubscribe = window.zabit.onUpdateStatus(setStatus);
    void window.zabit
      .getUpdateStatus()
      .then((current) => {
        setStatus(current);
        if (current.state !== 'ready' && current.state !== 'downloading') return window.zabit.checkForUpdates().then(setStatus);
      })
      .catch(() => {});
    return unsubscribe;
  }, []);

  const retry = () => {
    void window.zabit.checkForUpdates().then(setStatus).catch(() => {});
  };

  const install = () => {
    window.zabit.installUpdate().catch(() => retry());
  };

  let body: React.ReactNode;
  if (IS_NATIVE) {
    // Mobil: yeni sürüm mağazadan (IS_WEB de true; sayfayı yenilemek paketi değiştirmez).
    body = (
      <button onClick={() => void window.zabit.storeBilling?.openStorePage()} className={primaryCls}>
        {t('Mağazadan güncelle', 'Update from the store')}
      </button>
    );
  } else if (IS_WEB) {
    body = (
      <button onClick={() => window.location.reload()} className={primaryCls}>
        {t('Sayfayı yenile', 'Reload the page')}
      </button>
    );
  } else if (status?.state === 'ready') {
    body = (
      <button onClick={install} className={primaryCls}>
        {t(`Yeniden başlat ve ${status.version ?? 'yeni sürümü'} kur`, `Restart and install ${status.version ?? 'the update'}`)}
      </button>
    );
  } else if (status?.state === 'downloading' || status?.state === 'checking' || !status) {
    body = (
      <p className="flex items-center justify-center gap-2 text-[13px] text-neutral-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        {status?.state === 'downloading'
          ? t(`Güncelleme iniyor… %${Math.round(status.percent ?? 0)}`, `Downloading the update… ${Math.round(status.percent ?? 0)}%`)
          : t('Güncelleme denetleniyor…', 'Checking for updates…')}
      </p>
    );
  } else {
    // up-to-date (yayınlanan sürüm henüz eşiğe ulaşmadı), error, unsupported (geliştirme sürümü)
    body = (
      <div className="space-y-3">
        <p className="text-[12px] text-rose-400 leading-relaxed">
          {status.state === 'error' && status.message
            ? status.message
            : t('Otomatik güncelleme şu an bulunamadı.', 'No automatic update is available right now.')}{' '}
          {t('Yeni sürümü talknote.net’ten indirebilirsin.', 'You can download the new version from talknote.net.')}
        </p>
        <button onClick={retry} className={primaryCls}>
          {t('Tekrar dene', 'Try again')}
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex items-center justify-center text-[#F0EEE6] px-6">
      <div className="w-full max-w-sm space-y-5 text-center">
        <div className="space-y-2">
          <h1 className="text-[17px] font-semibold">{t('Güncelleme gerekli', 'Update required')}</h1>
          <p className="text-[13.5px] text-neutral-300 leading-relaxed">
            {minVersion
              ? t(
                  `TalkNote’un bu sürümü artık desteklenmiyor. Devam etmek için ${minVersion} ya da daha yeni bir sürüme güncelle.`,
                  `This version of TalkNote is no longer supported. Update to ${minVersion} or later to continue.`
                )
              : t('TalkNote’un bu sürümü artık desteklenmiyor. Devam etmek için güncelle.', 'This version of TalkNote is no longer supported. Please update to continue.')}
          </p>
          <p className="text-[12px] text-neutral-500 leading-relaxed">
            {t('Toplantıların ve notların güvende; güncelledikten sonra hepsi yerinde.', 'Your meetings and notes are safe; they’ll all be there after the update.')}
          </p>
        </div>
        {body}
        <button onClick={onSignOut} className="block mx-auto text-[12.5px] text-neutral-500 hover:text-neutral-300 transition-colors">
          {t('Çıkış yap', 'Sign out')}
        </button>
      </div>
    </div>
  );
};

const primaryCls =
  'px-4 py-2 rounded-xl bg-[#F0EEE6] text-neutral-900 font-semibold text-[13px] hover:bg-white transition-colors';
